import { AfterViewInit, Component, ElementRef, OnDestroy, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  createChart, CandlestickSeries, LineSeries, BarSeries, AreaSeries,
  HistogramSeries, createSeriesMarkers, ColorType, CrosshairMode, LineStyle,
} from 'lightweight-charts';
import { ChartDrawingController } from '../common/chart-drawing/chart-drawing-controller';
import { getToolLabel } from '../common/chart-drawing/drawing-tools.catalog';
import { DrawingTextDialogComponent } from '../common/chart-drawing/drawing-text-dialog/drawing-text-dialog.component';
import { SseService } from 'src/app/services/SseService';
import { firstValueFrom, Subscription } from 'rxjs';
import { CookieService } from 'ngx-cookie-service';
import { TemaService } from 'src/app/services/tema.services';
import { MatDialog } from '@angular/material/dialog';
import { ChatIaDialogComponent } from '../chat-ia-dialog/chat-ia-dialog.component';
import { ToastrService } from 'ngx-toastr';
import { urlMercado } from 'src/app/utils/mercado-url.util';

@Component({
  selector: 'app-stock',
  templateUrl: './stock.component.html',
  styleUrls: ['./stock.component.scss']
})
export class StockComponent implements AfterViewInit, OnDestroy {
  @ViewChild('chartContainer', { static: true }) chartContainer!: ElementRef<HTMLDivElement>;
  chart: any;
  candlestickSeries: any;
  symbol = 'AAPL';
  companies: any[] = [];
  selectedCompanyId: any = null;
  token: any;
  markets: any[] = [];
  selectedMarketId: number = 8; // default
  fecha: string | null = null;
  condicionJson: any = {"periodoModeloPeriodico":-1,"stopLossActivo":true,"periodo":6,"stopLossDinamicActivo":true,"start":"2010-01-01","nocondiciones":false,"bajista":false,"idnectums":[5239],"diasSalida":0,"licencia":"04130e3b-02c7-49e3-a7f3-13d73877f576","idmercado":8,"proFitActivo":true,"stopLoss":20,"list_conditions_exit":[],"end":"2026-01-25","repeticionentradas":false,"profit":20,"diasSalidaActivo":false,"list_conditions_entry":[]};
  
  // Price info
  currentPrice: number = 0;
  priceChange: number = 0;
  priceChangePercent: number = 0;
  
  // Side panel
  sidePanelOpen: boolean = false;
  activePanelSection: string | null = null;

  // Left panel resize
  leftPanelWidth = 300;
  private resizeStartX = 0;
  private resizeStartWidth = 0;
  private resizeMoveHandler: ((e: MouseEvent) => void) | null = null;
  private resizeUpHandler: (() => void) | null = null;
  
  // Indicator series tracking (for cleanup between runs)
  private indicatorSeries: any[] = [];
  private markersPlugin: any = null;

  // Run state
  isRunning = false;
  tradeStats: any = null;

  // Favorites
  favorites: any[] = [];
  showFavorites = false;
  showSaveFav = false;
  favoriteName = '';

  // Share
  copiedShare = false;

  // End date
  fechaFin: string | null = null;

  // DSL presets for casual users
  readonly conditionPresets = [
    { label: 'Precio > 100', dsl: '#price:_close > 100' },
    { label: 'SMA 20 > SMA 50', dsl: '#sma:_close:20 > #sma:_close:50' },
    { label: 'RSI < 30', dsl: '#rsi:_close:14 < 30' },
    { label: 'RSI > 70', dsl: '#rsi:_close:14 > 70' },
    { label: 'Sin condición', dsl: '' },
  ];

  // Built-in technical indicators
  readonly indicatorPresets = [
    { key: 'sma20',  label: 'SMA 20',         color: '#1E88E5', pane: 'main' },
    { key: 'sma50',  label: 'SMA 50',         color: '#FF9800', pane: 'main' },
    { key: 'sma200', label: 'SMA 200',        color: '#9C27B0', pane: 'main' },
    { key: 'ema20',  label: 'EMA 20',         color: '#4CAF50', pane: 'main' },
    { key: 'ema50',  label: 'EMA 50',         color: '#F44336', pane: 'main' },
    { key: 'bb20',   label: 'Bollinger 20',   color: '#FF6F00', pane: 'main' },
    { key: 'volume', label: 'Volumen',        color: '#26a69a', pane: 'sub'  },
    { key: 'rsi14',  label: 'RSI 14',         color: '#7C4DFF', pane: 'sub'  },
    { key: 'macd',   label: 'MACD (12,26,9)', color: '#00BCD4', pane: 'sub'  },
  ];
  activeIndicators: Record<string, boolean> = {};
  private builtinIndicatorSeries: Map<string, any> = new Map();
  historicalData: any[] = [];

  // Drawing tools
  drawingLines: {series: any, price: number}[] = [];
  drawingLinePrice: number | null = null;

  // Alerts
  alertsList: any[] = [];
  alertPrice: number | null = null;
  alertDirection = 'above';

  // Settings
  showGrid = true;
  logScale = false;

  // Chart toolbar
  chartType: 'candlestick' | 'line' | 'bar' | 'area' = 'candlestick';
  private rawData: number[][] = [];

  // Drawing tools (lógica compartida estilo TradingView)
  drawingCtl = new ChartDrawingController();

  // Theme
  isDark: boolean = false;
  private themeSubscription?: Subscription;

  // Admin — selector de condiciones IA
  isAdmin: boolean = false;
  condicionesAdmin: any[] = [];
  selectedCondicionIndex: number = -1;
  selectedCondicionObj: any = null;
  selectedCondicionesList: any[] = [];
  isLoadingCondiciones: boolean = false;
  isAdminRunning: boolean = false;
  adminCondicionTab: 'ideas' | 'tecnicos' | 'ratios' | 'patrones' = 'ideas';
  indicadoresAdmin: any[] = [];
  selectedIndicadorObj: any = null;
  adminMetaApi: boolean = false;
  adminPeriodo: number = 6; // 6 = 1d (default)
  condicionesEntradaList: string[] = [];
  currentCondicionInput: string = '';
  menuDefinitivo: any[] = [];
  private chatIaDialogRef: any = null;
  private adminLastFetchedPeriodo: number | null = null;
  private adminLastFetchedMetaApi: boolean | null = null;
  readonly periodos = [
    { value: 1, label: '1m' }, { value: 2, label: '5m' },
    { value: 3, label: '15m' }, { value: 4, label: '30m' },
    { value: 5, label: '1h' }, { value: 7, label: '4h' },
    { value: 6, label: '1d' }, { value: 8, label: '1w' },
  ];
  private adminIndicatorSeriesMap: Map<string, any> = new Map();
  private adminOscillatorPane: any = null;

  // ── Chunked history loading (igual que grafica-modelo Fase 3/4) ──────────
  private readonly DAY_MS = 24 * 60 * 60 * 1000;
  private readonly INITIAL_WINDOW_DAYS = 180; // default para 4h/1d/1w
  private readonly INITIAL_WINDOW_DAYS_BY_PERIODO: Record<number, number> = {
    1: 10,   // 1m
    2: 30,   // 5m
    3: 60,   // 15m
    4: 90,   // 30m
    5: 120,  // 1h
  };
  private fullPriceHistory: number[][] = []; // filas crudas ya cargadas, orden ascendente
  private loadedFrom: number | null = null;
  private loadedTo: number | null = null;
  private noMoreOlderHistory = false;
  private noMoreNewerHistory = false;
  private olderChunkInFlight = false;
  private newerChunkInFlight = false;
  private historyMutationLock = false;
  private visibleRangeHandler: ((range: any) => void) | null = null;

  // Analysis configuration
  analysisStopLoss = 20;
  analysisProfit = 20;
  analysisDiasSalida = 0;
  analysisBajista = false;
  analysisStopLossActivo = true;
  analysisStopLossDinamico = true;
  analysisProFitActivo = true;
  analysisDiasSalidaActivo = false;
  analysisSalidaFinDia = false;
  analysisRepeticionEntradas = false;
  analysisSectype = 'STK';
  onAnalysisTipoEstrategiaChange(event: any) {
    this.analysisBajista = event?.value === 'bajista';
  }
  private readonly oscillators: string[] = [
    'OBV', 'Percent_Return', 'SSF', 'CCI', 'KST', 'Fisher', 'RVGI', 'CDL_Pattern',
    'Squeeze_Pro', 'Quantile', 'CTI', 'TRIX', 'APO', 'STC', 'LinReg', 'Chop', 'PVO',
    'Slope', 'AO', 'KDJ', 'Aroon', 'Skew', 'CMO', 'QQE', 'RSI', 'StochRSI', 'Stochastic',
    'UO', 'StDev', 'ER', 'BRAR', 'Entropy', 'RSX', 'PGO', 'ERI', 'Williams_%R', 'PPO',
    'AMAT', 'Log_Return', 'TSI', 'Squeeze', 'CDL_Z', 'MACD', 'ROC', 'PSL', 'SWMA',
    'EBSW', 'Momentum', 'Variance', 'Coppock', 'TD_SEQ', 'Volume', 'volume', 'ADX',
    'BOP', 'MCGD', 'ATR', 'vol: historica', 'vol: relativa', 'vol', 'vol:_historica',
    '#vol:_historica', 'vol:_relativa',
  ];
  private readonly indicatorColors: string[] = [
    '#1E88E5','#FF9800','#9C27B0','#F44336','#4CAF50',
    '#00BCD4','#FF5722','#8BC34A','#E91E63','#03A9F4',
  ];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private _sseService: SseService,
    private _cookieService: CookieService,
    private temaService: TemaService,
    private dialog: MatDialog,
    private toastr: ToastrService
  ) { }

  ngAfterViewInit(): void {
    this.token = this._cookieService.get('auth_token');
    this.isAdmin = this._cookieService.get('user_type_id') === '1';
    if (this.isAdmin) { this.loadAdminCondiciones(); }
    this._sseService.apiPostNew('menu', {}, this.token).subscribe({
      next: (data: any) => { this.menuDefinitivo = data; },
      error: () => {}
    });
    this.symbol = this.route.snapshot.paramMap.get('symbol') || 'AAPL';
    
    // Subscribe to theme changes
    this.themeSubscription = this.temaService.valDarkTheme.subscribe(isDark => {
      this.isDark = isDark;
      if (this.chart) {
        this.updateChartTheme();
      }
    });

    // ensure condicionJson has minimal structure
    if (!this.condicionJson) this.condicionJson = {};
    if (!Array.isArray(this.condicionJson.list_conditions_entry)) this.condicionJson.list_conditions_entry = [];
    if (this.condicionJson.list_conditions_entry.length === 0) this.condicionJson.list_conditions_entry.push({ dsl: '' });
    // sync fecha/fechaFin with condicionJson
    this.fecha = this.condicionJson.start || null;
    this.fechaFin = this.condicionJson.end || null;

    // Read URL query params (shared links)
    const qp = this.route.snapshot.queryParamMap;

    // Short link: ?share=<id> — fetch state from server and restore
    const qShare = qp.get('share');
    if (qShare) {
      this._sseService.apiGetWithAuth(`share/stock/${qShare}`, this.token).subscribe({
        next: (s: any) => {
          if (s.dsl) this.conditionDsl = s.dsl;
          if (s.start) this.fecha = s.start;
          if (s.end) this.fechaFin = s.end;
          if (s.market != null) this.selectedMarketId = Number(s.market);
          if (s.periodo != null) this.adminPeriodo = Number(s.periodo);
          if (s.sl != null) this.analysisStopLoss = s.sl;
          if (s.pt != null) this.analysisProfit = s.pt;
          if (s.ds != null) this.analysisDiasSalida = s.ds;
          if (s.baj != null) this.analysisBajista = s.baj;
          if (s.sla != null) this.analysisStopLossActivo = s.sla;
          if (s.sld != null) this.analysisStopLossDinamico = s.sld;
          if (s.pta != null) this.analysisProFitActivo = s.pta;
          if (s.dsa != null) this.analysisDiasSalidaActivo = s.dsa;
          if (s.sfd != null) this.analysisSalidaFinDia = s.sfd;
          if (s.re != null) this.analysisRepeticionEntradas = s.re;
          if (s.sectype) this.analysisSectype = s.sectype;
          if (s.ct) this.chartType = s.ct;
          if (Array.isArray(s.ind)) s.ind.forEach((k: string) => { this.activeIndicators[k] = true; });
          // Reload companies for the restored market
          this.loadCompanies(this.selectedMarketId);
          this.toastr.info('Configuración restaurada desde enlace compartido', '', { timeOut: 3000 });
        },
        error: () => this.toastr.error('No se pudo cargar el enlace compartido', '')
      });
    }

    const qDsl = qp.get('dsl');
    const qStart = qp.get('start');
    const qEnd = qp.get('end');
    if (qDsl) this.conditionDsl = qDsl;
    if (qStart) { this.fecha = qStart; this.condicionJson.start = qStart; }
    if (qEnd) { this.fechaFin = qEnd; this.condicionJson.end = qEnd; }

    // Load saved favorites and alerts
    this.loadFavoritesFromStorage();
    this.loadAlerts();

    // Load markets first, then companies for the selected market
    this.loadMarkets().then(() => {
      // ensure selectedMarketId has a sensible default
      if (!this.selectedMarketId && this.markets?.length) this.selectedMarketId = this.markets[0].idmercado || 8;
      const initialMarket = this.markets.find((m: any) => m.idmercado == this.selectedMarketId);
      this.adminMetaApi = initialMarket?.metaapi ?? false;
      this.analysisSectype = initialMarket?.sectype ?? 'STK';
      // Load companies for selected market
      this.loadCompanies(this.selectedMarketId).then(() => {
      // If route didn't provide symbol, pick first company iso
      if (!this.route.snapshot.paramMap.get('symbol') && this.companies?.length) {
        const first = this.companies[0];
        this.selectedCompanyId = first.idNectum;
        this.symbol = first.iso || this.symbol;
      } else {
        // try to sync selectedCompanyId with existing symbol
        const found = this.companies.find(c => c.iso === this.symbol);
        if (found) this.selectedCompanyId = found.idNectum;
      }

        // Small delay to ensure container dimensions are ready
        setTimeout(() => { this.initChart(); }, 100);
      });
    });
  }

  // getter returns all conditions joined (used for saving favorites and URL params)
  get conditionDsl(): string {
    return this.condicionesEntradaList.join(' AND ');
  }
  // setter restores list when loading a favorite
  set conditionDsl(v: string) {
    if (!this.condicionJson) this.condicionJson = {};
    if (v) {
      this.condicionJson.list_conditions_entry = [{ dsl: v }];
      this.condicionesEntradaList = [v];
    } else {
      this.condicionJson.list_conditions_entry = [];
      this.condicionesEntradaList = [];
    }
    this.currentCondicionInput = v || '';
  }

  onFechaChange(v: string) {
    this.fecha = v;
    if (!this.condicionJson) this.condicionJson = {};
    this.condicionJson.start = v;
  }

  onFechaFinChange(v: string) {
    this.fechaFin = v;
    if (!this.condicionJson) this.condicionJson = {};
    this.condicionJson.end = v;
  }

  getSelectedCompanyName(): string {
    if (!this.selectedCompanyId || !this.companies?.length) return this.symbol || '';
    const company = this.companies.find(c => c.idNectum === this.selectedCompanyId);
    return company?.iso || company?.nombre || this.symbol || '';
  }

  getSelectedMarketName(): string {
    if (!this.selectedMarketId || !this.markets?.length) return '';
    const market = this.markets.find(m => m.idmercado === this.selectedMarketId);
    return market?.iso || market?.nombre || '';
  }

  toggleSidePanel(section: string) {
    if (this.activePanelSection === section) {
      this.sidePanelOpen = false;
      this.activePanelSection = null;
    } else {
      this.sidePanelOpen = true;
      this.activePanelSection = section;
    }
    // Resize chart after panel animation
    setTimeout(() => this.resizeChart(), 350);
  }

  closeSidePanel() {
    this.sidePanelOpen = false;
    this.activePanelSection = null;
    // Resize chart after panel animation
    setTimeout(() => this.resizeChart(), 350);
  }

  resizeChart() {
    if (this.chart && this.chartContainer) {
      const container = this.chartContainer.nativeElement;
      const width = container.clientWidth || window.innerWidth;
      const height = container.clientHeight || (window.innerHeight - 60);
      try { 
        this.chart.resize(width, height); 
        if (this.chart.timeScale) {
          this.chart.timeScale().fitContent();
        }
      } catch { }
    }
  }

  getChartColors() {
    // Paleta oscura alineada a los tokens --nec-* (bg #1e2328 / surface #2a2f35)
    return this.isDark ? {
      background: '#1e2328',
      textColor: '#d0d0d0',
      gridColor: '#2a2f35',
      upColor: '#26a69a',
      downColor: '#ef5350',
      wickUpColor: '#26a69a',
      wickDownColor: '#ef5350'
    } : {
      background: '#ffffff',
      textColor: '#333333',
      gridColor: '#e0e0e0',
      upColor: '#26a69a',
      downColor: '#ef5350',
      wickUpColor: '#26a69a',
      wickDownColor: '#ef5350'
    };
  }

  updateChartTheme() {
    if (!this.chart) return;
    const colors = this.getChartColors();
    
    this.chart.applyOptions({
      layout: {
        background: { color: colors.background },
        textColor: colors.textColor,
      },
      grid: {
        vertLines: { color: colors.gridColor },
        horzLines: { color: colors.gridColor },
      },
    });

    if (this.candlestickSeries) {
      this.candlestickSeries.applyOptions({
        upColor: colors.upColor,
        downColor: colors.downColor,
        wickUpColor: colors.wickUpColor,
        wickDownColor: colors.wickDownColor,
      });
    }
  }

  async loadCompanies(idmercado: number = 8): Promise<void> {
    try {
      const jsonData = { idmercado: idmercado, idNectum: 0 };
      const that = this;
      return new Promise((resolve) => {
        this._sseService.apiPostNew('listadecompaniaspormercado', jsonData, this.token).subscribe({
          next(data: any) {
            that.companies = Array.isArray(data) ? data : [];
            resolve();
          },
          error() { that.companies = []; resolve(); }
        });
      });
    } catch (err) {
      this.companies = [];
    }
  }

  async loadMarkets(): Promise<void> {
    try {
      const that = this;
      return new Promise((resolve) => {
        this._sseService.apiPostNew('listamercados', {}, this.token).subscribe({
          next(data: any) {
            that.markets = Array.isArray(data) ? data : [];
            resolve();
          },
          error() { that.markets = []; resolve(); }
        });
      });
    } catch (err) {
      this.markets = [];
    }
  }

  onMarketChange(event: any) {
    if (!this.selectedMarketId) return;
    const market = this.markets.find((m: any) => m.idmercado == this.selectedMarketId);
    this.adminMetaApi = market?.metaapi ?? false;
    this.analysisSectype = market?.sectype ?? 'STK';
    // reload companies for new market and reset selection
    this.loadCompanies(this.selectedMarketId).then(() => {
      this.selectedCompanyId = null;
      if (this.chart) {
        try { this.chart.remove(); } catch { }
      }
      // re-init chart after companies loaded
      setTimeout(() => this.initChart(), 50);
    });
  }

  onCompanyChange(event: any) {
    const sel = this.companies.find(c => c.idNectum == this.selectedCompanyId);
    if (sel) {
      this.reloadForSymbol(sel.iso);
    }
  }

  handleButtonClick(event: { label: string; isChecked: string; r: string }) {
    this.currentCondicionInput = event.r === 't' ? event.label.toLowerCase() : event.label;
  }

  openChatIaCondicion() {
    if (this.chatIaDialogRef) return;
    this.chatIaDialogRef = this.dialog.open(ChatIaDialogComponent, {
      width: '33vw',
      height: '70vh',
      panelClass: 'chat-ia-panel',
      disableClose: true,
      hasBackdrop: false,
      data: {
        condicionActual: this.currentCondicionInput || '',
        token: this.token,
        onCondicionChange: (condicion: string) => {
          this.currentCondicionInput = condicion;
          this.addDslCondicion(condicion);
        }
      }
    });
    this.chatIaDialogRef.afterClosed().subscribe(() => { this.chatIaDialogRef = null; });
  }

  saveCurrentCondicion() {
    this.addDslCondicion(this.currentCondicionInput);
    this.currentCondicionInput = '';
    this.runCondicionesEntrada();
  }

  private async runCondicionesEntrada(): Promise<void> {
    if (this.condicionesEntradaList.length === 0) {
      this.clearAdminIndicators();
      return;
    }
    const idNectum = this.selectedCompanyId
      || this.companies.find((c: any) => c.iso === this.symbol)?.idNectum;
    if (!idNectum) { alert('Seleccione un activo primero'); return; }

    const needsCandles = this.adminLastFetchedPeriodo !== this.adminPeriodo
      || this.adminLastFetchedMetaApi !== this.adminMetaApi;
    if (needsCandles) {
      await this.refetchAdminCandles();
    } else {
      this.clearAdminIndicators();
    }

    const combinedDsl = this.condicionesEntradaList.join(' AND ');
    const payload: any = {
      ...this.condicionJson,
      idnectums: [Number(idNectum)],
      list_conditions_entry: [{ dsl: combinedDsl }],
      list_conditions_exit: [],
      metaapi: this.adminMetaApi,
      periodo: this.adminPeriodo,
    };
    if (this.fecha) payload.start = this.fecha;
    if (this.fechaFin) payload.end = this.fechaFin;

    this.isAdminRunning = true;
    this._sseService.apiPostNew('calculo/cast_conditions', payload, this.token).subscribe({
      next: (data: any) => {
        this.isAdminRunning = false;
        try { this.renderAdminCastResult(data); } catch (e) { console.error(e); }
      },
      error: (err: any) => {
        this.isAdminRunning = false;
        console.warn('cast_conditions error', err);
      }
    });
  }

  addDslCondicion(dsl: string) {
    if (!dsl?.trim()) return;
    if (!this.condicionJson) this.condicionJson = {};
    if (!Array.isArray(this.condicionJson.list_conditions_entry)) this.condicionJson.list_conditions_entry = [];
    this.condicionJson.list_conditions_entry.push({ dsl: dsl.trim() });
    this.condicionesEntradaList = this.condicionJson.list_conditions_entry.map((c: any) => c.dsl).filter(Boolean);
  }

  removeDslCondicion(index: number) {
    if (!Array.isArray(this.condicionJson?.list_conditions_entry)) return;
    this.condicionJson.list_conditions_entry.splice(index, 1);
    this.condicionesEntradaList = [...this.condicionJson.list_conditions_entry.map((c: any) => c.dsl).filter(Boolean)];
    this.runCondicionesEntrada();
  }

  executeCastConditionsFromUI() {
    const idNectum = this.selectedCompanyId || (this.companies.find(c => c.iso === this.symbol)?.idNectum);
    if (!idNectum) {
      alert('Seleccione un activo primero');
      return;
    }

    const payload: any = {
      list_conditions_entry: Array.isArray(this.condicionJson?.list_conditions_entry)
        ? this.condicionJson.list_conditions_entry : [],
      list_conditions_exit: Array.isArray(this.condicionJson?.list_conditions_exit)
        ? this.condicionJson.list_conditions_exit : [],
      start: this.fecha || '2010-01-01',
      end: this.fechaFin || new Date().toISOString().split('T')[0],
      idnectums: [Number(idNectum)],
      stopLoss: this.analysisStopLoss,
      profit: this.analysisProfit,
      bajista: this.analysisBajista,
      periodo: this.adminPeriodo,
      idmercado: this.selectedMarketId,
      metaapi: this.adminMetaApi,
      sectype: this.analysisSectype,
      ln: [],
      periodoModeloPeriodico: -1,
      nocondiciones: false,
      repeticionentradas: this.analysisRepeticionEntradas,
      stopLossActivo: this.analysisStopLossActivo,
      stopLossDinamicActivo: this.analysisStopLossDinamico,
      proFitActivo: this.analysisProFitActivo,
      diasSalidaActivo: this.analysisDiasSalidaActivo,
      diasSalida: this.analysisDiasSalida,
      salidaFinDiaActivo: this.analysisSalidaFinDia,
      licencia: this.token
    };

    this.isRunning = true;
    try {
      this._sseService.apiPostNew('calculo/crear_modelo', payload, this.token).subscribe({
        next: (data: any) => { this.isRunning = false; this.handleCrearModeloResult(data); },
        error: (err: any) => { this.isRunning = false; console.warn('crear_modelo error', err); }
      });
    } catch (err) { this.isRunning = false; console.error('executeCastConditionsFromUI error', err); }
  }

  reloadForSymbol(newSymbol: string) {
    this.symbol = newSymbol;
    this.drawingCtl.dispose();
    if (this.chart) {
      try { this.chart.remove(); } catch { }
    }
    this.chartType = 'candlestick';
    this.indicatorSeries = [];
    this.markersPlugin = null;
    this.builtinIndicatorSeries.clear();
    this.activeIndicators = {};
    this.drawingLines = [];
    this.adminLastFetchedPeriodo = null;
    this.adminLastFetchedMetaApi = null;
    this.historicalData = [];
    this.rawData = [];
    // re-init chart for new symbol
    setTimeout(() => this.initChart(), 50);
    // update URL to reflect selected symbol
    try {
      this.router.navigate(['/stock', newSymbol]);
    } catch (e) { }
  }

  async initChart() {
    const container = this.chartContainer.nativeElement;
    console.log(`Initializing chart for ${this.symbol}. Container width: ${container.clientWidth}, height: ${container.clientHeight}`);
    
    // Use container dimensions directly
    const chartWidth = container.clientWidth || window.innerWidth;
    const chartHeight = container.clientHeight || (window.innerHeight - 60);

    const colors = this.getChartColors();

    this.chart = createChart(container, {
      width: chartWidth,
      height: chartHeight,
      layout: {
        background: { color: colors.background },
        textColor: colors.textColor,
      },
      grid: {
        vertLines: { color: colors.gridColor },
        horzLines: { color: colors.gridColor },
      },
    });

    this.chart.applyOptions({
      rightPriceScale: { scaleMargins: { top: 0.1, bottom: 0.1 } },
    });

    this.candlestickSeries = this.chart.addSeries(CandlestickSeries, {
      upColor: colors.upColor,
      downColor: colors.downColor,
      borderVisible: false,
      wickUpColor: colors.wickUpColor,
      wickDownColor: colors.wickDownColor,
    });

    // Attach drawing tools controller (toolbar TV, edición, persistencia)
    try { this.attachDrawingController(container); } catch (e) { console.warn('attachDrawingController error', e); }

    // Carga por ventana (chunked), igual que grafica-modelo — reset del estado de paginación
    this.fullPriceHistory = [];
    this.loadedFrom = null;
    this.loadedTo = null;
    this.noMoreOlderHistory = false;
    this.noMoreNewerHistory = false;
    this.olderChunkInFlight = false;
    this.newerChunkInFlight = false;
    this.historyMutationLock = false;

    try {
      const { desde, hasta } = this.computeInitialWindow();
      this.loadedFrom = desde;
      this.loadedTo = hasta;
      const url = `historicos-compania?iso=${encodeURIComponent(this.symbol)}&metaapi=${this.adminMetaApi}&periodo=${this.adminPeriodo}&desde=${desde}&hasta=${hasta}`;
      console.log('Fetching data from:', url);
      const API: any = await firstValueFrom(this._sseService.apiGetNew(url));
      console.log('API Response received for', this.symbol, '- Total rows:', API?.length);

      this.fullPriceHistory = this.normalizeHistoryRows(Array.isArray(API) ? API : []);
      this.rawData = this.fullPriceHistory;
      const validData = this.buildOhlcFromHistory(this.fullPriceHistory);

      if (!validData.length) {
        console.warn('No hay datos válidos, usando datos demo');
        this.setDemoData();
      } else {
        console.log(`Cargando ${validData.length} velas únicas para ${this.symbol}`);
        const isIntraday = this.isIntradayPeriodo();
        this.chart.applyOptions({
          timeScale: {
            timeVisible: isIntraday,
            secondsVisible: false,
            minBarSpacing: isIntraday ? 0.05 : 0.5,
          }
        });
        this.candlestickSeries.setData(validData);
        this.historicalData = validData;
        this.drawingCtl.restoreFromStorage();

        // Calculate current price and change
        if (validData.length >= 2) {
          const lastBar = validData[validData.length - 1];
          const prevBar = validData[validData.length - 2];
          this.currentPrice = lastBar.close;
          this.priceChange = lastBar.close - prevBar.close;
          this.priceChangePercent = (this.priceChange / prevBar.close) * 100;
        }
      }

      // ensure chart fits data and has correct size
      try {
        // fit the time scale to the data
        if (this.chart && this.chart.timeScale) this.chart.timeScale().fitContent();
        // force resize in case initial size was incorrect
        const width = container.clientWidth || window.innerWidth;
        const height = container.clientHeight || (window.innerHeight - 60);
        this.chart.resize(width, height);
      } catch (e) { console.warn('Error fitting/resizing chart', e); }

      // cargar más velas (viejas o nuevas) al hacer scroll/zoom cerca del borde cargado
      this.visibleRangeHandler = (range: any) => {
        if (!range) return;
        this.maybeLoadOlderChunk(range);
        this.maybeLoadNewerChunk(range);
      };
      this.chart.timeScale().subscribeVisibleLogicalRangeChange(this.visibleRangeHandler);
    } catch (err) {
      console.error('Error cargando históricos:', err);
      this.setDemoData();
    }

    // after loading data (or demo), call cast_conditions to get indicators for this asset
    try {
      const idNectum = this.selectedCompanyId || (this.companies.find(c => c.iso === this.symbol)?.idNectum);
      if (idNectum) {
        this.callCastConditions(Number(idNectum));
      }
    } catch (e) { /* ignore */ }

    window.addEventListener('resize', this.onResize);
  }

  callCastConditions(idNectum: number) {
    try {
      const payload: any = {
        idnectums: [Number(idNectum)],
        list_conditions_entry: [],
        list_conditions_exit: []
      };
      this._sseService.apiPostNew('calculo/cast_conditions', payload, this.token).subscribe({
        next: (data: any) => {
          try { this.handleCastResult(data); } catch (e) { console.error(e); }
        },
        error: (err: any) => { console.warn('cast_conditions error', err); }
      });
    } catch (err) {
      console.error('callCastConditions error', err);
    }
  }

  clearIndicators() {
    this.indicatorSeries.forEach(s => { try { this.chart.removeSeries(s); } catch {} });
    this.indicatorSeries = [];
    if (this.markersPlugin) {
      try { this.markersPlugin.setMarkers([]); } catch {}
    }
  }

  /**
   * Handles the crear_modelo response: Array<{idnectum, result, operaciones, descripciones}>
   * - operaciones → real ENTRADA/SALIDA markers with actual prices
   * - result → indicator overlay line series
   */
  handleCrearModeloResult(data: any) {
    if (!Array.isArray(data) || !data.length) { console.warn('crear_modelo: respuesta vacía o inválida', data); return; }
    const firstItem = data[0];
    if (firstItem?.error) { console.warn('crear_modelo error:', firstItem.message, firstItem.condition); return; }

    this.clearIndicators();
    const markers: any[] = [];

    // --- Operaciones reales (ENTRADA / SALIDA) ---
    const getOpFecha = (op: any): string => {
      if (typeof op.fechaOperacion === 'number') return new Date(op.fechaOperacion).toISOString().slice(0, 10);
      return String(op.fechaOperacion).slice(0, 10);
    };

    const operaciones: any[] = firstItem.operaciones || [];
    operaciones.forEach((op: any) => {
      if (!op.fechaOperacion) return;
      const fecha = getOpFecha(op);

      if (op.tipoOperacion === 'ENTRADA') {
        const price = op.precioentradareal != null ? ` @${(+op.precioentradareal).toFixed(2)}` : '';
        markers.push({ time: fecha, position: 'belowBar', color: '#26a69a', shape: 'arrowUp', text: `E${price}` });
      } else if (op.tipoOperacion === 'SALIDA') {
        const pnl = op.beneficioperdidapor != null
          ? ` ${(+op.beneficioperdidapor) >= 0 ? '+' : ''}${(+op.beneficioperdidapor).toFixed(1)}%`
          : '';
        const color = op.beneficioperdida != null && (+op.beneficioperdida) >= 0 ? '#26a69a' : '#ef5350';
        markers.push({ time: fecha, position: 'aboveBar', color, shape: 'arrowDown', text: `S${pnl}` });
      }
    });

    // --- Líneas de conexión ENTRADA → SALIDA ---
    const linEntradas = operaciones.filter(op => op.tipoOperacion === 'ENTRADA' && op.fechaOperacion);
    const linSalidas  = operaciones.filter(op => op.tipoOperacion === 'SALIDA'  && op.fechaOperacion);
    const maxPairs = Math.min(linEntradas.length, linSalidas.length);
    for (let i = 0; i < maxPairs; i++) {
      const entrada = linEntradas[i];
      const salida  = linSalidas[i];
      const entryDate = getOpFecha(entrada);
      const exitDate  = getOpFecha(salida);
      const entryPrice = +(entrada.precioentradareal ?? entrada.cotizacion ?? 0);
      const exitPrice  = +(salida.preciosalidareal  || salida.cotizacion  || 0);
      if (!entryDate || !exitDate || !isFinite(entryPrice) || !isFinite(exitPrice) || entryPrice === 0 || exitPrice === 0) continue;
      if (entryDate > exitDate) continue;
      const isProfit = (+salida.beneficioperdida) >= 0;
      const lineColor = isProfit ? 'rgba(38,166,154,0.75)' : 'rgba(239,83,80,0.75)';
      try {
        const s = this.chart.addSeries(LineSeries, {
          color: lineColor,
          lineWidth: 2,
          priceLineVisible: false,
          lastValueVisible: false,
          crosshairMarkerVisible: false,
        });
        s.setData([
          { time: entryDate, value: entryPrice },
          { time: exitDate,  value: exitPrice  },
        ]);
        this.indicatorSeries.push(s);
      } catch (e) { console.warn('Error drawing trade line', e); }
    }

    // --- Indicadores del result (columnas numéricas) ---
    const result: any[] = firstItem.result || [];
    if (result.length) {
      const keysToSkip = new Set(['close', 'low', 'high', 'open', 'volume', 'fecha']);
      // Keep DSL indicator columns (dsl_0_IDNECTUM:VALUE) but skip boolean decision cols
      const keys = Object.keys(result[0]).filter(k => {
        if (keysToSkip.has(k)) return false;
        if (k.includes('decision') || k.includes('entry_decision') || k.includes('exit_decision')) return false;
        // Skip cols that duplicate close price (e.g. dsl_0_XXXX:#price:_close)
        if (k.includes('#price:_close')) return false;
        return true;
      });

      const colors = ['#1E88E5', '#FF9800', '#9C27B0', '#F44336', '#4CAF50'];
      keys.forEach((key, idx) => {
        const seriesData = result
          .map((item: any) => {
            let val = item[key];
            if (typeof val === 'string' && val.toUpperCase() === 'NAN') val = null;
            if (typeof val === 'number' && !Number.isFinite(val)) val = null;
            return { time: (new Date(item.fecha)).toISOString().slice(0, 10), value: val };
          })
          .filter(p => p.value !== null && p.value !== undefined)
          .map(p => ({ time: p.time, value: Number(p.value) }));

        if (!seriesData.length) return;
        try {
          const s = this.chart.addSeries(LineSeries, { color: colors[idx % colors.length], lineWidth: 1, title: key });
          s.setData(seriesData);
          this.indicatorSeries.push(s);
        } catch (e) { console.warn('Error adding indicator series', key, e); }
      });
    }

    // Set markers sorted by time
    if (markers.length) {
      markers.sort((a, b) => a.time.localeCompare(b.time));
      try {
        if (this.markersPlugin) {
          this.markersPlugin.setMarkers(markers);
        } else {
          this.markersPlugin = createSeriesMarkers(this.candlestickSeries, markers);
        }
      } catch (e) { console.warn('setMarkers error', e); }
    }

    // --- Compute trade statistics ---
    const salidas = operaciones.filter(op => op.tipoOperacion === 'SALIDA');
    if (salidas.length) {
      const wins = salidas.filter(op => (+op.beneficioperdida) >= 0).length;
      const totalPnl = salidas.reduce((sum: number, op: any) => sum + (+op.beneficioperdidapor || 0), 0);
      this.tradeStats = {
        total: salidas.length,
        wins,
        losses: salidas.length - wins,
        winRate: (wins / salidas.length) * 100,
        totalPnl,
      };
    } else {
      this.tradeStats = null;
    }
  }

  handleCastResult(indicadorsIa: any) {
    if (!indicadorsIa || !indicadorsIa.result || !Array.isArray(indicadorsIa.result) || !indicadorsIa.result.length) return;
    const keys = Object.keys(indicadorsIa.result[0]);
    const keysToRemove = ['close', 'low', 'high', 'open', 'volume', 'fecha', 'dsl_0'];
    const ketss = keys.filter(k => !keysToRemove.includes(k));

    const markers: any[] = [];

    ketss.forEach(key => {
      const points: Array<{ time: string; value: any }> = [];
      indicadorsIa.result.forEach((item: any) => {
        let val: any = item[key];
        if (typeof val === 'string' && val.toUpperCase() === 'NAN') val = null;
        if (typeof val === 'number' && !Number.isFinite(val)) val = null;
        const timeStr = (new Date(item.fecha)).toISOString().slice(0, 10);
        points.push({ time: timeStr, value: val });
      });

      if (key.includes('entry') || key.includes('exit') || key.includes('decision')) {
        points.forEach(p => {
          if (p.value === true) {
            markers.push({ time: p.time, position: key.includes('entry') ? 'belowBar' : 'aboveBar', color: key.includes('entry') ? 'green' : 'red', shape: key.includes('entry') ? 'arrowUp' : 'arrowDown', text: key.includes('entry') ? 'E' : 'S' });
          }
        });
      } else {
        try {
          const series = this.chart.addSeries(LineSeries, { color: '#1E88E5', lineWidth: 1 });
          const seriesData = points.filter(p => p.value !== null && p.value !== undefined).map(p => ({ time: p.time, value: Number(p.value) }));
          if (seriesData.length) series.setData(seriesData);
        } catch (e) { console.warn('Error adding indicator series', e); }
      }
    });

    // derive trade entry/exits from the cast_conditions result and add markers
    try {
      const tradeMarkers = this.processTradesFromCast(indicadorsIa.result);
      markers.push(...tradeMarkers);
    } catch (e) { console.warn('Error processing trades from cast', e); }

    if (markers.length) {
      try {
        if (this.markersPlugin) {
          this.markersPlugin.setMarkers(markers);
        } else {
          this.markersPlugin = createSeriesMarkers(this.candlestickSeries, markers);
        }
      } catch (e) { console.warn('Error setting markers', e); }
    }
  }

  /**
   * Process cast_conditions result rows to create entry/exit markers.
   * Rules applied:
   * - When an element has `entry_decision_final === true` and there is no open position,
   *   create an entry on the NEXT bar (use its open or close as entry price).
   * - Compute a stoploss = entryPrice - 20 and scan forward to find the first bar
   *   where low <= stoploss (stop triggered) OR `exit_decision_final === true` (exit signal).
   * - Record markers for entry (belowBar, 'E') and exit (aboveBar, 'S').
   */
  processTradesFromCast(rows: any[]): any[] {
    const markers: any[] = [];
    if (!Array.isArray(rows) || rows.length === 0) return markers;

    // ensure rows sorted by fecha ascending
    const data = [...rows].sort((a, b) => new Date(a.fecha).getTime() - new Date(b.fecha).getTime());

    let positionOpen = false;
    let entryPrice = 0;
    for (let i = 0; i < data.length; i++) {
      const row = data[i];
      // entry decision refers to creating an entry on the next bar
      if (!positionOpen && row.entry_decision_final === true) {
        const next = data[i + 1];
        if (!next) continue; // no next bar available
        // prefer open price for entry, fallback to close
        const price = (next.open !== undefined && next.open !== null) ? Number(next.open) : Number(next.close);
        if (!isFinite(price)) continue;
        entryPrice = price;
        positionOpen = true;
        const entryTime = (new Date(next.fecha)).toISOString().slice(0, 10);
        markers.push({ time: entryTime, position: 'belowBar', color: 'green', shape: 'arrowUp', text: 'E' });

        // compute stoploss
        const stoploss = entryPrice - 20;

        // scan for exit
        let exited = false;
        for (let j = i + 1; j < data.length; j++) {
          const rj = data[j];
          const low = Number(rj.low);
          const close = Number(rj.close);
          const fecha = (new Date(rj.fecha)).toISOString().slice(0, 10);

          // stoploss triggered
          if (!isNaN(low) && low <= stoploss) {
            markers.push({ time: fecha, position: 'aboveBar', color: 'red', shape: 'arrowDown', text: 'S' });
            positionOpen = false;
            exited = true;
            break;
          }

          // explicit exit decision
          if (rj.exit_decision_final === true) {
            markers.push({ time: fecha, position: 'aboveBar', color: 'red', shape: 'arrowDown', text: 'S' });
            positionOpen = false;
            exited = true;
            break;
          }
        }

        if (!exited) {
          // if we never exited, close position at last bar
          const last = data[data.length - 1];
          const fecha = (new Date(last.fecha)).toISOString().slice(0, 10);
          markers.push({ time: fecha, position: 'aboveBar', color: 'red', shape: 'arrowDown', text: 'S' });
          positionOpen = false;
        }
      }
    }

    return markers;
  }

  loadFavoritesFromStorage() {
    try {
      const raw = localStorage.getItem('stock_favorites');
      this.favorites = raw ? JSON.parse(raw) : [];
    } catch { this.favorites = []; }
  }

  /** Abre el favorito en /mercados dentro del mercado en que se guardó. */
  urlMercadoFavorito(fav: any): string {
    const mercado = this.markets.find((m: any) => m.idmercado == fav.idmercado) ?? { idmercado: fav.idmercado };
    return urlMercado(mercado, fav.symbol ?? '');
  }

  saveFavorite() {
    if (!this.favoriteName.trim()) return;
    const fav = {
      name: this.favoriteName.trim(),
      dsl: this.conditionDsl,
      idNectum: this.selectedCompanyId,
      symbol: this.getSelectedCompanyName(),
      idmercado: this.selectedMarketId,
      start: this.fecha,
      end: this.fechaFin,
    };
    this.favorites = [...this.favorites, fav];
    localStorage.setItem('stock_favorites', JSON.stringify(this.favorites));
    this.favoriteName = '';
    this.showSaveFav = false;
    this.showFavorites = true;
  }

  deleteFavorite(i: number) {
    this.favorites = this.favorites.filter((_, idx) => idx !== i);
    localStorage.setItem('stock_favorites', JSON.stringify(this.favorites));
  }

  loadFavorite(fav: any) {
    this.conditionDsl = fav.dsl || '';
    if (fav.start) { this.fecha = fav.start; this.condicionJson.start = fav.start; }
    if (fav.end) { this.fechaFin = fav.end; this.condicionJson.end = fav.end; }
    if (fav.idNectum && fav.idmercado) {
      if (fav.idmercado !== this.selectedMarketId) {
        this.selectedMarketId = fav.idmercado;
        this.loadCompanies(this.selectedMarketId).then(() => {
          this.selectedCompanyId = fav.idNectum;
          this.reloadForSymbol(fav.symbol);
        });
      } else {
        this.selectedCompanyId = fav.idNectum;
        const sel = this.companies.find(c => c.idNectum == fav.idNectum);
        if (sel) this.reloadForSymbol(sel.iso);
      }
    }
  }

  loadPreset(preset: any) {
    this.conditionDsl = preset.dsl;
  }

  async shareCondition() {
    const state = {
      dsl: this.conditionDsl || null,
      start: this.fecha,
      end: this.fechaFin,
      market: this.selectedMarketId,
      periodo: this.adminPeriodo,
      sl: this.analysisStopLoss,
      pt: this.analysisProfit,
      ds: this.analysisDiasSalida,
      baj: this.analysisBajista,
      sla: this.analysisStopLossActivo,
      sld: this.analysisStopLossDinamico,
      pta: this.analysisProFitActivo,
      dsa: this.analysisDiasSalidaActivo,
      sfd: this.analysisSalidaFinDia,
      re: this.analysisRepeticionEntradas,
      sectype: this.analysisSectype,
      ct: this.chartType !== 'candlestick' ? this.chartType : null,
      ind: Object.entries(this.activeIndicators).filter(([, v]) => v).map(([k]) => k)
    };
    try {
      const res: any = await firstValueFrom(
        this._sseService.apiPostNew('share/stock', state, this.token)
      );
      const sym = this.getSelectedCompanyName();
      const url = `${window.location.origin}/stock/${encodeURIComponent(sym)}?share=${res.id}`;
      await navigator.clipboard.writeText(url);
      this.toastr.success('Enlace copiado al portapapeles', 'Compartir');
    } catch {
      this.toastr.error('No se pudo generar el enlace compartido', 'Compartir');
    }
  }

  // ─── Built-in Indicators ────────────────────────────────────────────────────

  computeSMA(period: number): {time: string, value: number}[] {
    const data = this.historicalData;
    if (!data || data.length < period) return [];
    const result: {time: string, value: number}[] = [];
    for (let i = period - 1; i < data.length; i++) {
      const sum = data.slice(i - period + 1, i + 1).reduce((acc: number, d: any) => acc + d.close, 0);
      result.push({ time: data[i].time, value: sum / period });
    }
    return result;
  }

  computeEMA(period: number): {time: string, value: number}[] {
    const data = this.historicalData;
    if (!data || data.length < period) return [];
    const k = 2 / (period + 1);
    let ema = data.slice(0, period).reduce((acc: number, d: any) => acc + d.close, 0) / period;
    const result: {time: string, value: number}[] = [{ time: data[period - 1].time, value: ema }];
    for (let i = period; i < data.length; i++) {
      ema = data[i].close * k + ema * (1 - k);
      result.push({ time: data[i].time, value: ema });
    }
    return result;
  }

  toggleIndicator(preset: {key: string, label: string, color: string, pane?: string}) {
    if (this.activeIndicators[preset.key]) {
      const entry = this.builtinIndicatorSeries.get(preset.key);
      if (entry) {
        const arr = Array.isArray(entry) ? entry : [entry];
        arr.forEach((s: any) => { try { this.chart.removeSeries(s); } catch {} });
      }
      this.builtinIndicatorSeries.delete(preset.key);
      this.activeIndicators = { ...this.activeIndicators, [preset.key]: false };
      return;
    }

    if (!this.historicalData.length) return;
    let added: any[] = [];
    try {
      if (preset.key.startsWith('sma')) {
        const d = this.computeSMA(parseInt(preset.key.replace('sma', '')));
        if (!d.length) return;
        const s = this.chart.addSeries(LineSeries, { color: preset.color, lineWidth: 1, title: preset.label, priceLineVisible: false, lastValueVisible: true, crosshairMarkerVisible: false });
        s.setData(d); added = [s];
      } else if (preset.key.startsWith('ema')) {
        const d = this.computeEMA(parseInt(preset.key.replace('ema', '')));
        if (!d.length) return;
        const s = this.chart.addSeries(LineSeries, { color: preset.color, lineWidth: 1, title: preset.label, priceLineVisible: false, lastValueVisible: true, crosshairMarkerVisible: false });
        s.setData(d); added = [s];
      } else if (preset.key === 'bb20') {
        const bands = this.computeBollinger(20, 2);
        if (!bands.length) return;
        const opts = { lineWidth: 1, priceLineVisible: false, lastValueVisible: false, crosshairMarkerVisible: false };
        const upper = this.chart.addSeries(LineSeries, { ...opts, color: preset.color, title: 'BB↑' });
        const mid   = this.chart.addSeries(LineSeries, { ...opts, color: preset.color, lineStyle: 2, title: 'BB mid' });
        const lower = this.chart.addSeries(LineSeries, { ...opts, color: preset.color, title: 'BB↓' });
        upper.setData(bands.map(b => ({ time: b.time, value: b.upper })));
        mid.setData(bands.map(b => ({ time: b.time, value: b.mid })));
        lower.setData(bands.map(b => ({ time: b.time, value: b.lower })));
        added = [upper, mid, lower];
      } else if (preset.key === 'volume') {
        const d = this.computeVolumeData();
        if (!d.length) return;
        const s = this.chart.addSeries(HistogramSeries, { priceFormat: { type: 'volume' }, priceScaleId: 'vol_ind' });
        this.chart.priceScale('vol_ind').applyOptions({ scaleMargins: { top: 0.8, bottom: 0 } });
        s.setData(d); added = [s];
      } else if (preset.key === 'rsi14') {
        const d = this.computeRSI(14);
        if (!d.length) return;
        const s = this.chart.addSeries(LineSeries, { color: preset.color, lineWidth: 1, title: 'RSI 14', priceScaleId: 'rsi', priceLineVisible: false, lastValueVisible: true, crosshairMarkerVisible: false });
        this.chart.priceScale('rsi').applyOptions({ scaleMargins: { top: 0.8, bottom: 0 } });
        s.setData(d); added = [s];
      } else if (preset.key === 'macd') {
        const { macd, signal, histogram } = this.computeMACD(12, 26, 9);
        if (!macd.length) return;
        const sMACD = this.chart.addSeries(LineSeries, { color: preset.color, lineWidth: 1, title: 'MACD', priceScaleId: 'macd', priceLineVisible: false, lastValueVisible: true, crosshairMarkerVisible: false });
        const sSig  = this.chart.addSeries(LineSeries, { color: '#FF7043', lineWidth: 1, title: 'Signal', priceScaleId: 'macd', priceLineVisible: false, lastValueVisible: false, crosshairMarkerVisible: false });
        const sHist = this.chart.addSeries(HistogramSeries, { title: 'Hist', priceScaleId: 'macd' });
        this.chart.priceScale('macd').applyOptions({ scaleMargins: { top: 0.85, bottom: 0 } });
        sMACD.setData(macd); sSig.setData(signal); sHist.setData(histogram);
        added = [sMACD, sSig, sHist];
      }
    } catch (e) { console.warn('toggleIndicator error', preset.key, e); return; }

    if (added.length) {
      this.builtinIndicatorSeries.set(preset.key, added.length === 1 ? added[0] : added);
      this.activeIndicators = { ...this.activeIndicators, [preset.key]: true };
    }
  }

  // ─── Range & Chart Type ──────────────────────────────────────────────────────

  setRange(period: string): void {
    if (!this.chart) return;
    if (period === 'all') { this.chart.timeScale().fitContent(); return; }
    if (!this.historicalData.length) return;
    const last = this.historicalData[this.historicalData.length - 1].time;
    const days = period === '1M' ? 30 : period === '3M' ? 90 : period === '6M' ? 180 : period === '1Y' ? 365 : 365 * 5;
    const from = new Date(new Date(last).getTime() - days * 86400000).toISOString().slice(0, 10);
    try { this.chart.timeScale().setVisibleRange({ from: from as any, to: last as any }); } catch {}
  }

  setChartType(type: 'candlestick' | 'line' | 'bar' | 'area'): void {
    if (!this.chart || !this.historicalData.length || type === this.chartType) return;
    this.chartType = type;
    if (this.candlestickSeries) { try { this.chart.removeSeries(this.candlestickSeries); } catch {} this.candlestickSeries = null; }
    const c = this.getChartColors();
    const d = this.historicalData;
    try {
      switch (type) {
        case 'candlestick':
          this.candlestickSeries = this.chart.addSeries(CandlestickSeries, { upColor: c.upColor, downColor: c.downColor, borderVisible: false, wickUpColor: c.wickUpColor, wickDownColor: c.wickDownColor });
          this.candlestickSeries.setData(d); break;
        case 'line':
          this.candlestickSeries = this.chart.addSeries(LineSeries, { color: c.upColor, lineWidth: 2 });
          this.candlestickSeries.setData(d.map((r: any) => ({ time: r.time, value: r.close }))); break;
        case 'bar':
          this.candlestickSeries = this.chart.addSeries(BarSeries, { upColor: c.upColor, downColor: c.downColor });
          this.candlestickSeries.setData(d); break;
        case 'area':
          this.candlestickSeries = this.chart.addSeries(AreaSeries, { lineColor: c.upColor, topColor: c.upColor + '44', bottomColor: c.upColor + '00' });
          this.candlestickSeries.setData(d.map((r: any) => ({ time: r.time, value: r.close }))); break;
      }
    } catch (e) { console.warn('setChartType error', e); }
    // La serie se recreó: reenganchar los dibujos
    if (this.candlestickSeries) this.drawingCtl.reattachSeries(this.candlestickSeries);
  }

  // ─── Drawing Tools (controller compartido) ───────────────────────────────────

  private attachDrawingController(container: HTMLElement): void {
    if (!this.chart || !this.candlestickSeries) return;
    this.drawingCtl.attach({
      chart: this.chart,
      series: this.candlestickSeries,
      container,
      storageKey: () =>
        this.symbol ? `nectum:drawings:v1:${this.symbol}:${this.adminPeriodo}` : null,
      formatTime: (ms: number) => {
        const isIntraday = this.adminPeriodo !== 6 && this.adminPeriodo !== 8;
        return (isIntraday ? Math.round(ms / 1000) : new Date(ms).toISOString().slice(0, 10)) as any;
      },
      getBarOHLC: (time: any) => {
        const target = String(time);
        const bar = this.historicalData.find((b: any) => String(b.time) === target);
        return bar ? { open: bar.open, high: bar.high, low: bar.low, close: bar.close } : null;
      },
      requestText: (toolType: string) => {
        const ref = this.dialog.open(DrawingTextDialogComponent, {
          data: { toolLabel: getToolLabel(toolType) },
        });
        return new Promise(resolve => {
          ref.afterClosed().subscribe((value: string | undefined) => resolve(value === undefined ? null : value));
        });
      },
      isDark$: this.temaService.valDarkTheme,
    });
  }

  // ─── Legacy static lines (kept for backward compat) ─────────────────────────

  addHorizontalLine() {
    const price = this.drawingLinePrice ?? this.currentPrice;
    if (!price || !this.historicalData.length) return;
    try {
      const s = this.chart.addSeries(LineSeries, {
        color: '#FFA726', lineWidth: 1, lineStyle: 2,
        priceLineVisible: false, lastValueVisible: true, crosshairMarkerVisible: false,
        title: price.toFixed(2),
      });
      s.setData([
        { time: this.historicalData[0].time, value: price },
        { time: this.historicalData[this.historicalData.length - 1].time, value: price },
      ]);
      this.drawingLines = [...this.drawingLines, { series: s, price }];
      this.drawingLinePrice = null;
    } catch {}
  }

  removeDrawingLine(i: number) {
    try { this.chart.removeSeries(this.drawingLines[i].series); } catch {}
    this.drawingLines = this.drawingLines.filter((_, idx) => idx !== i);
  }

  clearDrawings() {
    this.drawingLines.forEach(d => { try { this.chart.removeSeries(d.series); } catch {} });
    this.drawingLines = [];
  }

  // ─── Alerts ─────────────────────────────────────────────────────────────────

  loadAlerts() {
    try {
      const raw = localStorage.getItem('stock_alerts');
      this.alertsList = raw ? JSON.parse(raw) : [];
    } catch { this.alertsList = []; }
  }

  addAlert() {
    if (!this.alertPrice) return;
    const a = { price: this.alertPrice, direction: this.alertDirection, symbol: this.getSelectedCompanyName(), created: new Date().toISOString().slice(0, 10) };
    this.alertsList = [...this.alertsList, a];
    localStorage.setItem('stock_alerts', JSON.stringify(this.alertsList));
    this.alertPrice = null;
  }

  removeAlert(i: number) {
    this.alertsList = this.alertsList.filter((_, idx) => idx !== i);
    localStorage.setItem('stock_alerts', JSON.stringify(this.alertsList));
  }

  // ─── Chart Settings ──────────────────────────────────────────────────────────

  applyGridSetting() {
    if (!this.chart) return;
    const c = this.getChartColors();
    this.chart.applyOptions({
      grid: {
        vertLines: { color: this.showGrid ? c.gridColor : 'transparent' },
        horzLines: { color: this.showGrid ? c.gridColor : 'transparent' },
      }
    });
  }

  applyLogScale() {
    if (!this.chart) return;
    try { this.chart.priceScale('right').applyOptions({ mode: this.logScale ? 1 : 0 }); } catch {}
  }

  // ── Chunked history loading — helpers ─────────────────────────────────────
  private isIntradayPeriodo(): boolean {
    return this.adminPeriodo !== 6 && this.adminPeriodo !== 8;
  }

  private getInitialWindowDays(): number {
    return this.INITIAL_WINDOW_DAYS_BY_PERIODO[this.adminPeriodo] ?? this.INITIAL_WINDOW_DAYS;
  }

  private computeInitialWindow(): { desde: number; hasta: number } {
    const hasta = Date.now();
    return { desde: hasta - this.getInitialWindowDays() * this.DAY_MS, hasta };
  }

  /** Normaliza el timestamp (fila[0]) de filas crudas [ts, open, high, low, close, ...] a milisegundos. */
  private normalizeHistoryRows(rows: any[]): number[][] {
    if (!Array.isArray(rows)) return [];
    return rows
      .filter((row: any) => Array.isArray(row) && row.length >= 5)
      .map((row: any[]) => {
        const ts = Number(row[0]);
        const ms = ts > 1e11 ? ts : ts * 1000;
        const copy = [...row];
        copy[0] = ms;
        return copy;
      });
  }

  /** Convierte filas crudas (ms) a velas {time, open, high, low, close} ordenadas y sin duplicados. */
  private buildOhlcFromHistory(rows: number[][]): any[] {
    if (!Array.isArray(rows) || !rows.length) return [];
    const isIntraday = this.isIntradayPeriodo();
    const data = rows
      .map((row: number[]) => ({
        time: isIntraday ? Math.round(row[0] / 1000) : new Date(row[0]).toISOString().slice(0, 10),
        open: Number(row[1]), high: Number(row[2]), low: Number(row[3]), close: Number(row[4]),
      }))
      .filter(d => !isNaN(d.open) && !isNaN(d.high) && !isNaN(d.low) && !isNaN(d.close));
    data.sort((a, b) => (a.time > b.time ? 1 : a.time < b.time ? -1 : 0));
    return data.filter((d, i) => i === 0 || d.time !== data[i - 1].time);
  }

  private maybeLoadOlderChunk(range: { from: number; to: number }): void {
    if (this.olderChunkInFlight || this.noMoreOlderHistory) return;
    if (range.from > 10) return;
    this.fetchOlderChunk();
  }

  private async fetchOlderChunk(): Promise<void> {
    if (this.loadedFrom == null) return;
    this.olderChunkInFlight = true;
    try {
      const hasta = this.loadedFrom - 1;
      const desde = hasta - this.getInitialWindowDays() * this.DAY_MS;
      const url = `historicos-compania?iso=${encodeURIComponent(this.symbol)}&metaapi=${this.adminMetaApi}&periodo=${this.adminPeriodo}&desde=${desde}&hasta=${hasta}`;
      const rows: any = await firstValueFrom(this._sseService.apiGetNew(url));
      if (!Array.isArray(rows) || !rows.length) { this.noMoreOlderHistory = true; return; }
      this.mergeHistoryChunk(this.normalizeHistoryRows(rows), 'prepend');
      this.loadedFrom = desde;
    } catch (e) {
      console.warn('fetchOlderChunk error', e);
    } finally {
      this.olderChunkInFlight = false;
    }
  }

  private maybeLoadNewerChunk(range: { from: number; to: number }): void {
    const lastIndex = this.fullPriceHistory.length - 1;
    if (this.newerChunkInFlight || this.noMoreNewerHistory) return;
    if (lastIndex < 0 || range.to < lastIndex - 10) return;
    this.fetchNewerChunk();
  }

  private async fetchNewerChunk(): Promise<void> {
    if (this.loadedTo == null) return;
    if (this.loadedTo >= Date.now()) { this.noMoreNewerHistory = true; return; }
    this.newerChunkInFlight = true;
    try {
      const desde = this.loadedTo + 1;
      const hasta = Math.min(Date.now(), desde + this.getInitialWindowDays() * this.DAY_MS);
      const url = `historicos-compania?iso=${encodeURIComponent(this.symbol)}&metaapi=${this.adminMetaApi}&periodo=${this.adminPeriodo}&desde=${desde}&hasta=${hasta}`;
      const rows: any = await firstValueFrom(this._sseService.apiGetNew(url));
      if (!Array.isArray(rows) || !rows.length) { this.noMoreNewerHistory = true; return; }
      this.mergeHistoryChunk(this.normalizeHistoryRows(rows), 'append');
      this.loadedTo = hasta;
    } catch (e) {
      console.warn('fetchNewerChunk error', e);
    } finally {
      this.newerChunkInFlight = false;
    }
  }

  /** Mezcla un chunk nuevo (más viejo o más nuevo) en el histórico ya cargado, preservando el viewport. */
  private mergeHistoryChunk(newRows: number[][], direction: 'prepend' | 'append'): void {
    if (!this.chart || !this.candlestickSeries || !newRows.length) return;
    if (this.historyMutationLock) { setTimeout(() => this.mergeHistoryChunk(newRows, direction), 260); return; }
    this.historyMutationLock = true;
    try {
      const currentRange = this.chart.timeScale().getVisibleRange();
      this.fullPriceHistory = direction === 'prepend'
        ? [...newRows, ...this.fullPriceHistory].sort((a, b) => a[0] - b[0])
        : [...this.fullPriceHistory, ...newRows].sort((a, b) => a[0] - b[0]);
      this.rawData = this.fullPriceHistory;
      const merged = this.buildOhlcFromHistory(this.fullPriceHistory);
      this.candlestickSeries.setData(merged);
      this.historicalData = merged;
      if (currentRange) { try { this.chart.timeScale().setVisibleRange(currentRange); } catch {} }
    } finally {
      this.historyMutationLock = false;
    }
  }

  setDemoData() {
    const now = Date.now();
    const data: any[] = [];
    let prevClose = 100;
    for (let i = 30; i >= 0; i--) {
      const t = new Date(now - i * 24 * 60 * 60 * 1000);
      const date = t.toISOString().slice(0, 10);

      const open = prevClose + (Math.random() - 0.5) * 4;
      const close = open + (Math.random() - 0.5) * 4;
      const high = Math.max(open, close) + Math.random() * 2;
      const low = Math.min(open, close) - Math.random() * 2;

      data.push({
        time: date,
        open: Math.round(open * 100) / 100,
        high: Math.round(high * 100) / 100,
        low: Math.round(low * 100) / 100,
        close: Math.round(close * 100) / 100
      });
      prevClose = close;
    }
    this.candlestickSeries.setData(data);
  }

  computeRSI(period: number): { time: string; value: number }[] {
    const d = this.historicalData;
    if (!d || d.length <= period) return [];
    let gains = 0, losses = 0;
    for (let i = 1; i <= period; i++) {
      const ch = d[i].close - d[i - 1].close;
      if (ch > 0) gains += ch; else losses -= ch;
    }
    let avgGain = gains / period, avgLoss = losses / period;
    const result: { time: string; value: number }[] = [];
    result.push({ time: d[period].time, value: avgLoss === 0 ? 100 : 100 - 100 / (1 + avgGain / avgLoss) });
    for (let i = period + 1; i < d.length; i++) {
      const ch = d[i].close - d[i - 1].close;
      avgGain = (avgGain * (period - 1) + (ch > 0 ? ch : 0)) / period;
      avgLoss = (avgLoss * (period - 1) + (ch < 0 ? -ch : 0)) / period;
      result.push({ time: d[i].time, value: avgLoss === 0 ? 100 : 100 - 100 / (1 + avgGain / avgLoss) });
    }
    return result;
  }

  computeMACD(fast: number, slow: number, signalPeriod: number): { macd: any[]; signal: any[]; histogram: any[] } {
    const fastEMA = this.computeEMA(fast);
    const slowEMA = this.computeEMA(slow);
    const fastMap = new Map(fastEMA.map(d => [d.time, d.value]));
    const macdLine: { time: string; value: number }[] = [];
    slowEMA.forEach(d => { const f = fastMap.get(d.time); if (f !== undefined) macdLine.push({ time: d.time, value: f - d.value }); });
    if (macdLine.length < signalPeriod) return { macd: [], signal: [], histogram: [] };
    const k = 2 / (signalPeriod + 1);
    let sig = macdLine.slice(0, signalPeriod).reduce((s, d) => s + d.value, 0) / signalPeriod;
    const signalLine: { time: string; value: number }[] = [{ time: macdLine[signalPeriod - 1].time, value: sig }];
    for (let i = signalPeriod; i < macdLine.length; i++) {
      sig = macdLine[i].value * k + sig * (1 - k);
      signalLine.push({ time: macdLine[i].time, value: sig });
    }
    const sigMap = new Map(signalLine.map(d => [d.time, d.value]));
    const histogram = macdLine
      .filter(d => sigMap.has(d.time))
      .map(d => ({ time: d.time, value: d.value - sigMap.get(d.time)!, color: d.value >= sigMap.get(d.time)! ? '#26a69a88' : '#ef535088' }));
    return { macd: macdLine, signal: signalLine, histogram };
  }

  computeBollinger(period: number, mult: number): { time: string; upper: number; mid: number; lower: number }[] {
    const d = this.historicalData;
    if (!d || d.length < period) return [];
    const result: { time: string; upper: number; mid: number; lower: number }[] = [];
    for (let i = period - 1; i < d.length; i++) {
      const slice = d.slice(i - period + 1, i + 1);
      const mid = slice.reduce((s: number, r: any) => s + r.close, 0) / period;
      const std = Math.sqrt(slice.reduce((s: number, r: any) => s + Math.pow(r.close - mid, 2), 0) / period);
      result.push({ time: d[i].time, upper: mid + mult * std, mid, lower: mid - mult * std });
    }
    return result;
  }

  computeVolumeData(): { time: string; value: number; color: string }[] {
    if (!this.rawData.length) return [];
    const c = this.getChartColors();
    return this.rawData
      .map((r, i) => ({
        time: new Date(Number(r[0])).toISOString().slice(0, 10),
        value: isFinite(Number(r[r.length - 1])) ? Number(r[r.length - 1]) : 0,
        color: Number(r[4]) >= (i > 0 ? Number(this.rawData[i - 1][4]) : Number(r[4])) ? c.upColor + '88' : c.downColor + '88',
      }))
      .sort((a, b) => a.time.localeCompare(b.time))
      .reduce((acc: any[], d) => { if (!acc.length || acc[acc.length - 1].time !== d.time) acc.push(d); return acc; }, []);
  }

  onResize = () => {
    if (this.chart && this.chartContainer) {
      const container = this.chartContainer.nativeElement;
      const width = container.clientWidth || window.innerWidth;
      const height = container.clientHeight || (window.innerHeight - 60);
      try { 
        this.chart.resize(width, height); 
      } catch { }
    }
  };

  // ─── Admin: condiciones IA ───────────────────────────────────────────────

  loadAdminCondiciones() {
    this.loadAdminCondicionesByTab(this.adminCondicionTab);
  }

  loadAdminCondicionesByTab(tab: 'ideas' | 'tecnicos' | 'ratios' | 'patrones') {
    this.adminCondicionTab = tab;
    this.indicadoresAdmin = [];
    this.selectedIndicadorObj = null;
    this.condicionesAdmin = [];
    this.selectedCondicionIndex = -1;
    this.selectedCondicionObj = null;
    this.isLoadingCondiciones = true;

    if (tab === 'ideas') {
      // IDEAS: carga directo en condicionesAdmin (sin select de indicador intermedio)
      this._sseService.apiGetNew('conditions/condiciones').subscribe({
        next: (res: any) => {
          this.condicionesAdmin = res?.condiciones ?? (Array.isArray(res) ? res : []);
          this.isLoadingCondiciones = false;
        },
        error: () => { this.isLoadingCondiciones = false; }
      });
    } else {
      // TÉCNICOS / RATIOS / PATRONES: carga indicadores en el select intermedio
      const params = tab === 'tecnicos'
        ? 'tecnico=true&patrones=false'
        : tab === 'ratios'
          ? 'tecnico=false&patrones=false'
          : 'tecnico=true&patrones=true';

      this._sseService.apiPostNew(`listaindicadoresportecnico3?${params}`, {}, this.token).subscribe({
        next: (res: any) => {
          this.indicadoresAdmin = Array.isArray(res) ? res : [];
          this.isLoadingCondiciones = false;
        },
        error: () => { this.isLoadingCondiciones = false; }
      });
    }
  }

  onAdminIndicadorSelectChange(item: any): void {
    if (!item?.indicador) return;
    this.condicionesAdmin = [];
    this.selectedCondicionIndex = -1;
    this.selectedCondicionObj = null;
    this.isLoadingCondiciones = true;

    this._sseService.apiPostNew('condicionesporindicador', { indicador: item.indicador }, this.token).subscribe({
      next: (res: any) => {
        this.condicionesAdmin = res?.condiciones ?? [];
        this.isLoadingCondiciones = false;
      },
      error: () => { this.isLoadingCondiciones = false; }
    });
  }

  onAdminCondicionChange() {
    if (this.selectedCondicionIndex >= 0) {
      this.runCondicionAtIndex(this.selectedCondicionIndex);
    }
  }

  onAdminCondicionSelectChange(item: any) {
    if (!item) { this.selectedCondicionIndex = -1; return; }
    const idx = this.condicionesAdmin.indexOf(item);
    this.selectedCondicionIndex = idx;
    if (idx >= 0) this.runCondicionAtIndex(idx);
  }

  isCondicionSelected(c: any): boolean {
    return this.selectedCondicionesList.some(x => x.indicador === c.indicador);
  }

  selectCondicionRow(idx: number): void {
    const c = this.condicionesAdmin[idx];
    const existing = this.selectedCondicionesList.findIndex(x => x.indicador === c.indicador);
    if (existing >= 0) {
      this.selectedCondicionesList = this.selectedCondicionesList.filter((_, i) => i !== existing);
    } else {
      this.selectedCondicionesList = [...this.selectedCondicionesList, c];
    }
    this.selectedCondicionIndex = idx;
    this.runSelectedCondiciones();
  }

  toggleCondicionCheck(idx: number, event: Event): void {
    event.stopPropagation();
    const c = this.condicionesAdmin[idx];
    const existing = this.selectedCondicionesList.findIndex(x => x.indicador === c.indicador);
    if (existing >= 0) {
      this.selectedCondicionesList = this.selectedCondicionesList.filter((_, i) => i !== existing);
    } else {
      this.selectedCondicionesList = [...this.selectedCondicionesList, c];
    }
    this.selectedCondicionIndex = idx;
    this.runSelectedCondiciones();
  }

  removeCondicionFromList(item: any): void {
    this.selectedCondicionesList = this.selectedCondicionesList.filter(x => x.indicador !== item.indicador);
    this.runSelectedCondiciones();
  }

  private async runSelectedCondiciones(): Promise<void> {
    if (this.selectedCondicionesList.length === 0) {
      this.clearAdminIndicators();
      return;
    }

    const idNectum = this.selectedCompanyId
      || this.companies.find((c: any) => c.iso === this.symbol)?.idNectum;
    if (!idNectum) { alert('Seleccione un activo primero'); return; }

    const needsCandles = this.adminLastFetchedPeriodo !== this.adminPeriodo
      || this.adminLastFetchedMetaApi !== this.adminMetaApi;
    if (needsCandles) {
      await this.refetchAdminCandles();
    } else {
      this.clearAdminIndicators();
    }

    const combinedDsl = this.selectedCondicionesList.map(c => c.indicador).join(' AND ');
    const list_conditions_entry = [{ dsl: combinedDsl }];

    const payload: any = {
      ...this.condicionJson,
      idnectums: [Number(idNectum)],
      list_conditions_entry,
      list_conditions_exit: [],
      metaapi: this.adminMetaApi,
      periodo: this.adminPeriodo,
    };
    if (this.fecha) payload.start = this.fecha;
    if (this.fechaFin) payload.end = this.fechaFin;

    this.isAdminRunning = true;
    this._sseService.apiPostNew('calculo/cast_conditions', payload, this.token).subscribe({
      next: (data: any) => {
        this.isAdminRunning = false;
        try { this.renderAdminCastResult(data); } catch (e) { console.error(e); }
      },
      error: (err: any) => {
        this.isAdminRunning = false;
        console.warn('cast_conditions (admin) error', err);
      }
    });
  }

  prevCondicion() {
    if (this.selectedCondicionIndex > 0) {
      this.selectedCondicionIndex--;
      this.selectedCondicionObj = this.condicionesAdmin[this.selectedCondicionIndex];
      this.runCondicionAtIndex(this.selectedCondicionIndex);
    }
  }

  nextCondicion() {
    if (this.selectedCondicionIndex < this.condicionesAdmin.length - 1) {
      this.selectedCondicionIndex++;
      this.selectedCondicionObj = this.condicionesAdmin[this.selectedCondicionIndex];
      this.runCondicionAtIndex(this.selectedCondicionIndex);
    }
  }

  onAdminPeriodoChange() {
    if (this.selectedCondicionIndex >= 0) {
      // condición ya seleccionada → re-fetch velas + cast_conditions con nuevo periodo
      this.runCondicionAtIndex(this.selectedCondicionIndex);
    } else {
      // sin condición → solo re-fetch velas para mostrar el nuevo timeframe
      this.refetchAdminCandles();
    }
  }

  private async refetchAdminCandles() {
    // Cambió el periodo/metaapi → reiniciar la ventana de carga por partes
    this.fullPriceHistory = [];
    this.loadedFrom = null;
    this.loadedTo = null;
    this.noMoreOlderHistory = false;
    this.noMoreNewerHistory = false;
    this.olderChunkInFlight = false;
    this.newerChunkInFlight = false;
    this.historyMutationLock = false;

    const { desde, hasta } = this.computeInitialWindow();
    const candleUrl = `historicos-compania?iso=${encodeURIComponent(this.symbol)}&metaapi=${this.adminMetaApi}&periodo=${this.adminPeriodo}&desde=${desde}&hasta=${hasta}`;
    try {
      const API: any = await firstValueFrom(this._sseService.apiGetNew(candleUrl));
      if (Array.isArray(API) && API.length) {
        this.loadedFrom = desde;
        this.loadedTo = hasta;
        this.fullPriceHistory = this.normalizeHistoryRows(API);
        this.rawData = this.fullPriceHistory;
        const unique = this.buildOhlcFromHistory(this.fullPriceHistory);
        if (unique.length) {
          this.adminLastFetchedPeriodo = this.adminPeriodo;
          this.adminLastFetchedMetaApi = this.adminMetaApi;
          this.clearAdminIndicators();
          const isIntraday = this.isIntradayPeriodo();
          // Configurar timeScale igual que grafica-modelo (líneas 1857-1861)
          this.chart.applyOptions({
            timeScale: {
              timeVisible: isIntraday,
              secondsVisible: false,
              minBarSpacing: isIntraday ? 0.05 : 0.5,
            }
          });
          this.candlestickSeries.setData(unique);
          this.historicalData = unique;
          // el periodo pudo cambiar → clave de storage distinta
          this.drawingCtl.restoreFromStorage();
          try { this.chart.timeScale().fitContent(); } catch {}
        }
      }
    } catch (e) { console.warn('Admin candle refetch error', e); }
  }

  private async runCondicionAtIndex(idx: number) {
    const condicion = this.condicionesAdmin[idx];
    if (!condicion?.indicador) return;
    const idNectum = this.selectedCompanyId
      || this.companies.find((c: any) => c.iso === this.symbol)?.idNectum;
    if (!idNectum) { alert('Seleccione un activo primero'); return; }

    // Re-fetch velas solo si cambió el activo, periodo o metaapi
    const needsCandles = this.adminLastFetchedPeriodo !== this.adminPeriodo
      || this.adminLastFetchedMetaApi !== this.adminMetaApi;
    if (needsCandles) {
      await this.refetchAdminCandles(); // ya llama clearAdminIndicators internamente
    } else {
      this.clearAdminIndicators(); // limpiar series de la condición anterior
    }

    // cast_conditions con payload completo (igual que grafica-modelo)
    const payload: any = {
      ...this.condicionJson,
      idnectums: [Number(idNectum)],
      list_conditions_entry: [{ dsl: condicion.indicador }],
      list_conditions_exit: [],
      metaapi: this.adminMetaApi,
      periodo: this.adminPeriodo,
    };
    if (this.fecha) payload.start = this.fecha;
    if (this.fechaFin) payload.end = this.fechaFin;

    this.isAdminRunning = true;
    this._sseService.apiPostNew('calculo/cast_conditions', payload, this.token).subscribe({
      next: (data: any) => {
        this.isAdminRunning = false;
        try { this.renderAdminCastResult(data); } catch (e) { console.error(e); }
      },
      error: (err: any) => {
        this.isAdminRunning = false;
        console.warn('cast_conditions (admin) error', err);
      }
    });
  }

  private clearAdminIndicators() {
    this.adminIndicatorSeriesMap.forEach(series => {
      try { this.chart.removeSeries(series); } catch {}
    });
    this.adminIndicatorSeriesMap.clear();

    if (this.adminOscillatorPane) {
      try { (this.chart as any).removePane(this.adminOscillatorPane); } catch {}
      this.adminOscillatorPane = null;
    }

    if (this.markersPlugin) {
      try { this.markersPlugin.setMarkers([]); } catch {}
    }
  }

  // Réplica de grafica-modelo applyIndicadorsIaIncremental
  private renderAdminCastResult(indicadorsIa: any): void {
    if (!indicadorsIa || !Array.isArray(indicadorsIa.result) || !indicadorsIa.result.length) return;

    const keysToRemove = new Set(['close','low','high','open','volume','fecha','dsl_0']);
    const allKeys = Object.keys(indicadorsIa.result[0] ?? {});
    const ketss = allKeys.filter(k =>
      !keysToRemove.has(k) &&
      !k.startsWith('pattern_') &&
      !k.includes('decision')   // excluye entry_decision_* y exit_decision_* (igual que grafica-modelo isDecision check)
    );

    // Agrupar datos por clave con timestamp unix ms (igual que grafica-modelo)
    const grouped: { [key: string]: { unixFecha: number; valor: any }[] } = {};
    for (const key of ketss) { grouped[key] = []; }

    indicadorsIa.result.forEach((item: any) => {
      const unixFecha = Date.parse(item.fecha);
      ketss.forEach(key => {
        let valor = item[key];
        if (typeof valor === 'string' && valor.toUpperCase() === 'NAN') valor = null;
        else if (typeof valor === 'number' && !Number.isFinite(valor)) valor = null;
        grouped[key].push({ unixFecha, valor });
      });
    });

    // Conversión de tiempo: intraday → unix segundos, diario → 'YYYY-MM-DD'
    const isIntraday = this.adminPeriodo !== 6 && this.adminPeriodo !== 8;
    const toChartTime = (ms: number): any =>
      isIntraday ? Math.round(ms / 1000) : new Date(ms).toISOString().slice(0, 10);

    // ── Pass 1: detectar prefijos osciladores (igual que grafica-modelo) ──────
    const oscillatorPrefixSet = new Set<string>();
    for (const key of ketss) {
      const condicionbuscar = key.replaceAll('#', '').split(':')[1];
      if (!condicionbuscar) continue;
      let isOsc = this.oscillators.some(s => condicionbuscar.toLowerCase().includes(s.toLowerCase()));
      if (key.toLowerCase().includes('sma:_')) isOsc = false;
      if (isOsc) oscillatorPrefixSet.add(key.split(':')[0]);
    }

    // ── Pass 2: clasificar cada clave ─────────────────────────────────────────
    const arrayAxis: { isOscillator: boolean }[] = ketss.map(key => {
      const condicionbuscar = key.replaceAll('#', '').split(':')[1];
      let isOscillator = false;
      if (condicionbuscar) {
        isOscillator = this.oscillators.some(s => condicionbuscar.toLowerCase().includes(s.toLowerCase()));
        if (key.toLowerCase().includes('sma:_')) isOscillator = false;
        if (!isOscillator && oscillatorPrefixSet.has(key.split(':')[0])) isOscillator = true;
      }
      return { isOscillator };
    });

    // ── Graficar cada indicador ───────────────────────────────────────────────
    let colorIdx = 0;
    ketss.forEach((key, i) => {
      const lineData = grouped[key]
        .filter(p => p.valor !== null && p.valor !== undefined && Number.isFinite(Number(p.valor)))
        .map(p => ({ time: toChartTime(p.unixFecha), value: Number(p.valor) }))
        .sort((a: any, b: any) => String(a.time).localeCompare(String(b.time)))
        .reduce((acc: any[], item: any) => {
          if (!acc.length || acc[acc.length - 1].time !== item.time) acc.push(item);
          return acc;
        }, []);

      if (!lineData.length) return;

      const isOscillator = arrayAxis[i]?.isOscillator ?? false;
      const color = this.indicatorColors[colorIdx++ % this.indicatorColors.length];
      try {
        const existing = this.adminIndicatorSeriesMap.get(key);
        if (existing) {
          existing.setData(lineData);
        } else {
          let series: any;
          if (isOscillator) {
            if (!this.adminOscillatorPane) {
              this.adminOscillatorPane = (this.chart as any).addPane();
            }
            series = this.adminOscillatorPane.addSeries(LineSeries, {
              color, lineWidth: 1, title: key,
              priceLineVisible: false, lastValueVisible: true, crosshairMarkerVisible: true,
            });
          } else {
            series = this.chart.addSeries(LineSeries, {
              color, lineWidth: 1, title: key,
              priceLineVisible: false, lastValueVisible: true, crosshairMarkerVisible: true,
              priceFormat: { type: 'price', precision: 5, minMove: 0.00001 },
            });
          }
          series.setData(lineData);
          this.adminIndicatorSeriesMap.set(key, series);
        }
      } catch (e) { console.warn('Error adding admin indicator series', key, e); }
    });
  }

  // ─────────────────────────────────────────────────────────────────────────

  ngOnDestroy(): void {
    window.removeEventListener('resize', this.onResize);
    this.drawingCtl.dispose();
    if (this.chart) {
      try { this.chart.remove(); } catch { }
    }
    if (this.themeSubscription) { this.themeSubscription.unsubscribe(); }
    this.cleanupResizeListeners();
  }

  startResize(event: MouseEvent): void {
    this.resizeStartX = event.clientX;
    this.resizeStartWidth = this.leftPanelWidth;

    this.resizeMoveHandler = (e: MouseEvent) => {
      const dx = e.clientX - this.resizeStartX;
      this.leftPanelWidth = Math.max(200, Math.min(520, this.resizeStartWidth + dx));
    };

    this.resizeUpHandler = () => {
      this.cleanupResizeListeners();
    };

    document.addEventListener('mousemove', this.resizeMoveHandler);
    document.addEventListener('mouseup', this.resizeUpHandler);
    event.preventDefault();
  }

  private cleanupResizeListeners(): void {
    if (this.resizeMoveHandler) {
      document.removeEventListener('mousemove', this.resizeMoveHandler);
      this.resizeMoveHandler = null;
    }
    if (this.resizeUpHandler) {
      document.removeEventListener('mouseup', this.resizeUpHandler);
      this.resizeUpHandler = null;
    }
  }
}
